import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  ConfirmationDialog,
  FilterToolbarSearch,
  filterToolbarSearchGrowClassName,
} from '@roger/ui-components';
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePayrollAccess } from '../components/payroll/usePayrollAccess';
import { useStore } from '../store';
import {
  exportPayrollEntryVisits,
  listPayrollEntryVisits,
  printPayrollEntryVisits,
  removePayrollEntryVisit,
} from '../services/api';
import type { PayrollVisitItem } from '../services/api/types.gen';
import { PaginationBar } from '../components/payroll/PaginationBar';
import { PayrollExportMenu } from '../components/payroll/PayrollExportMenu';
import { PayrollVisitsTable } from '../components/payroll/PayrollVisitsTable';
import { useToast } from '../hooks/useToastContext';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../utils/downloadFile';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { formatDateToMMDDYYYY } from '../utils/dateUtils';
import { useBreadcrumbs } from '../components/breadcrumbs/BreadcrumbContext';

const PAID_TAB_PATH = '/payroll-manager?tab=paid';

const PayrollDetail: React.FC = () => {
  const { payrollEntryId } = useParams<{ payrollEntryId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  // This screen is reachable by direct URL, so it gates the same as the tabs.
  const hasAccess = usePayrollAccess();
  useBreadcrumbs(hasAccess ? [{ label: 'Payroll Summary' }] : []);
  const hideImportedPatients = useStore(
    state => state.payrollFilters.hideImportedPatients !== false
  );
  const pageSize = useStore(state => state.payrollPageSizes.detail);
  const setPayrollPageSize = useStore(state => state.setPayrollPageSize);

  const [visits, setVisits] = useState<PayrollVisitItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [payrollDate, setPayrollDate] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [visitToRemove, setVisitToRemove] = useState<PayrollVisitItem | null>(
    null
  );
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchVisits = useCallback(async () => {
    if (!payrollEntryId || !hasAccess) return;
    setIsLoading(true);
    try {
      const query = {
        search: search || undefined,
        page,
        per_page: pageSize,
        hide_imported_patients: hideImportedPatients,
      };
      const response = await listPayrollEntryVisits({
        path: { payroll_entry_id: payrollEntryId },
        query,
        throwOnError: true,
      });
      setVisits(response.data.items);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
      setPayrollDate(response.data.payroll_date ?? null);
    } catch (error) {
      if (getHttpStatus(error) === 404) {
        toast.error('Payroll not found. It may have been deleted.');
        navigate(PAID_TAB_PATH, { replace: true });
        return;
      }
      console.error('Failed to load payroll visits:', error);
      toast.error(
        getBusinessErrorMessage(error) ??
          'Failed to load payroll visits. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
    // toast is intentionally omitted: useToast returns a fresh object every
    // render, so including it would refetch in a loop (OASISManager pattern).
  }, [
    payrollEntryId,
    hasAccess,
    search,
    page,
    pageSize,
    hideImportedPatients,
    navigate,
  ]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const handleRemoveVisit = async () => {
    if (!payrollEntryId || !visitToRemove) return;
    setIsRemoving(true);
    try {
      await removePayrollEntryVisit({
        path: {
          payroll_entry_id: payrollEntryId,
          visit_id: visitToRemove.visit_id,
        },
        throwOnError: true,
      });
      toast.success('Visit removed from payroll.');
      setVisitToRemove(null);
      // Step back a page when the removal emptied the current one.
      if (visits.length === 1 && page > 1) {
        setPage(p => p - 1);
      } else {
        await fetchVisits();
      }
    } catch (error) {
      console.error('Failed to remove visit from payroll:', error);
      toast.error(
        getBusinessErrorMessage(error) ??
          'Failed to remove visit. Please try again.'
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const handleExport = async () => {
    if (!payrollEntryId) return;
    try {
      const response = await exportPayrollEntryVisits({
        path: { payroll_entry_id: payrollEntryId },
        query: { hide_imported_patients: hideImportedPatients },
        responseType: 'blob',
        throwOnError: false,
      });
      if (response.error !== undefined) {
        // The server explains an over-cap or emptied payroll; a generic
        // message would leave the user retrying the same export.
        console.error('Failed to export payroll:', response.error);
        toast.error(
          await getUserFacingApiMessageAsync(
            response,
            'Failed to export. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response, 'RogerPayroll.xlsx');
    } catch (error) {
      console.error('Failed to export payroll:', error);
      toast.error(
        (await getUserFacingApiMessageAsync(
          error,
          'Failed to export. Please try again.'
        )) ?? 'Failed to export. Please try again.'
      );
    }
  };

  if (!hasAccess) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access Payroll Manager. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header (Intake3 detail-page pattern) */}
      <div>
        <div className='pb-2 border-b border-gray-100 flex items-center justify-between'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => navigate(PAID_TAB_PATH)}
            className='flex items-center gap-1.5'
          >
            <ArrowLeft className='h-3.5 w-3.5' />
            Back to Paid Payrolls
          </Button>

          {/* "Export All" (Excel or PDF), not just "Export": the search box
              below filters the table but neither file. A short file nobody
              realises was filtered is the one error finance cannot spot, so
              the menu label says so. */}
          <PayrollExportMenu
            label='Export All'
            onExportExcel={handleExport}
            fetchPdf={() =>
              payrollEntryId
                ? printPayrollEntryVisits({
                    path: { payroll_entry_id: payrollEntryId },
                    responseType: 'blob',
                  })
                : Promise.reject(new Error('No payroll to print'))
            }
            // Only `total === 0` with no search means the payroll is empty.
            // With a search active `total` is the filtered count, and this
            // export ignores the filter, so disabling on it would block a
            // valid export of the whole payroll because the search happened
            // to match nothing.
            disabled={!payrollEntryId || isLoading || (!search && total === 0)}
          />
        </div>
        <div className='pt-3'>
          <div className='text-sm text-muted-foreground'>
            {payrollDate && (
              <>
                Payroll date: {formatDateToMMDDYYYY(payrollDate)}
                {' · '}
              </>
            )}
            {total} visits
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className='flex flex-wrap gap-4 items-end'>
        <FilterToolbarSearch
          id='search'
          value={searchInput}
          onChange={setSearchInput}
          label='Search'
          placeholder='Search by patient or clinician...'
          containerClassName={filterToolbarSearchGrowClassName}
          className='w-full'
        />
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-16'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          <span className='ml-3 text-muted-foreground'>Loading visits...</span>
        </div>
      ) : visits.length === 0 ? (
        <div className='text-center text-muted-foreground py-8'>
          <p>No visits in this payroll</p>
          {search && <p className='text-sm'>Try adjusting your search</p>}
        </div>
      ) : (
        <div className='space-y-4'>
          <div className='rounded-md border'>
            <PayrollVisitsTable
              visits={visits}
              groupBy='visit_date'
              showClinicianColumn
              showClinicColumn
              onRemoveVisit={setVisitToRemove}
            />
          </div>

          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            itemsLabel='visits'
            onPageChange={setPage}
            onPageSizeChange={size => {
              setPayrollPageSize('detail', size);
              setPage(1);
            }}
          />
        </div>
      )}

      <ConfirmationDialog
        open={!!visitToRemove}
        title='Remove visit from payroll?'
        message={
          visitToRemove
            ? `${visitToRemove.patient_name} (${formatDateToMMDDYYYY(visitToRemove.visit_date)}) is released from this payroll. It goes back to Unpaid if its status is one your agency pays for.`
            : ''
        }
        confirmLabel='Remove'
        loading={isRemoving}
        onAccept={handleRemoveVisit}
        onCancel={() => setVisitToRemove(null)}
      />
    </div>
  );
};

export default PayrollDetail;
